import { Entity, Id, Paging, Context, Order, PaginatedResult } from '../../../common'
import { ROTransaction, RWTransaction } from '../../../db/db'

export enum UserStatus {
  /** Active, confirmed user */
  ACTIVE = 'active',
  /** Blocked user */
  BLOCKED = 'blocked',
}

export interface User extends Entity {
  email: string
  passwordHash: string
  surname: string
  name: string
  patronymic?: string
  controlQuestionId?: Id
  controlAnswerHash?: string
  phone?: string
  municipalEntityId?: Id
  status: UserStatus
  roleId: Id
  cardIssuePointId?: Id
}

export function usersIsEqual(u1?: User, u2?: User): boolean {
  if (u1 === u2) return true
  if (u1 === undefined || u2 === undefined) return false
  return (
    u1.id === u2.id &&
    u1.deletedAt?.getTime() === u2.deletedAt?.getTime() &&
    u1.name === u2.name &&
    u1.surname === u2.surname &&
    u1.patronymic === u2.patronymic &&
    u1.roleId === u2.roleId &&
    u1.status === u2.status &&
    u1.phone === u2.phone &&
    u1.municipalEntityId === u2.municipalEntityId &&
    u1.cardIssuePointId === u2.cardIssuePointId &&
    u1.controlQuestionId === u2.controlQuestionId
  )
}

export interface UserFilter {
  ids?: Id[]
  /**
   * Full text search by email, fullname, phone
   */
  search?: string
  email?: string
  roleId?: Id
  statuses?: UserStatus[]
  municipalEntityId?: Id
  cardIssuePointId?: Id
}

export enum UserOrderFields {
  CREATED_AT = 'createdAt',
  EMAIL = 'email',
}

export interface UserFindOptions {
  filter?: UserFilter
  order?: Order<UserOrderFields> | undefined
  paging?: Paging
}

export function userGetFullname(user: User) {
  return [user.surname, user.name, user.patronymic].filter(val => val).join(' ')
}

export interface UsersRepository {
  findAll(tx: ROTransaction, opts?: UserFindOptions): Promise<PaginatedResult<User>>
  findById(tx: ROTransaction, id: Id): Promise<User | undefined>
  findByIdNonDeleted(tx: ROTransaction, id: string): Promise<User | undefined>
  findByEmail(tx: ROTransaction, email: string): Promise<User | undefined>
  save(tx: RWTransaction, user: User): Promise<void>
  delete(tx: RWTransaction, id: Id, deletedAt: Date): Promise<void>
}

export type AddUserOpts = {
  id?: Id
  email: string
  password: string
  surname: string
  name: string
  controlQuestionId?: Id
  controlAnswer?: string
  patronymic?: string
  phone?: string
  municipalEntityId?: Id
  status: UserStatus
  roleId: Id
  cardIssuePointId?: Id
}

export type UpdateUserOpts = {
  password?: string
  surname?: string
  name?: string
  controlQuestionId?: Id
  controlAnswer?: string
  patronymic?: string
  phone?: string
  municipalEntityId?: Id
  status?: UserStatus
  roleId?: Id
  cardIssuePointId?: Id
}

export interface UsersService {
  findAll(ctx: Context, opts?: UserFindOptions): Promise<PaginatedResult<User>>
  findByIdNonDeleted(ctx: Context, id: Id): Promise<User | undefined>
  findByEmail(ctx: Context, email: string): Promise<User | undefined>
  findById(ctx: Context, id: Id): Promise<User | undefined>
  add(ctx: Context, opts: AddUserOpts): Promise<Id>
  update(ctx: Context, id: Id, opts: UpdateUserOpts): Promise<void>
  delete(ctx: Context, id: Id): Promise<void>
}
